import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../hooks/hooks";
import { LessonPlanDocumentType, LessonPlanType } from "../../types/LessonPlan";
import { useGetLessonPlansQuery } from "../../hooks/useGetLessonPlansQuery";
import { updateLessonPlan } from "../../services/school/lessonPlans/updateLessonPlan";
import { Key_lessonPlans } from "../../utils/constants";
import toast from "react-hot-toast";
import { routes } from "../../routes";
import { ArchiveIcon, ChevronLeftIcon, EnterIcon, Pencil1Icon, PlusIcon, TrashIcon } from "@radix-ui/react-icons";
import { Flex, Button, Card, Spinner, Text, Box, IconButton, Tooltip } from "@radix-ui/themes";
import { ConfirmAlertDialog } from "../../components/common/ConfirmAlertDialog";
import { EmptyState } from "../../components/common/EmptyState";
import { Lists } from "../../components/common/Lists";
import TableComponent from "../../components/common/Tables";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { createLessonPlan, createLessonPlanDocument } from "../../services/school/lessonPlans/createLessonPlan";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CreateLessonPlanFormValidation } from "./CreateLessonPlanFormValidation";
import TextAreaFieldComponent from "../../components/inputs/TextAreafield/TextAreafieldComponent";
import DateFieldComponent from "../../components/inputs/Datefield/DatefieldComponent";
import { DragAndDropFileUpload } from "../../components/inputs/FileUpload/DragAndDropFileUpload";
import { dataURLtoFile, getFile } from "../../utils/fileUtils";
import { formatDate, formatDateForInput, formatDateForOutput } from "../../utils/dateFormatter";
import { setLessonPlanData } from "../../redux/slices/school/lessonPlan";
import { getLessonPlansDocuments } from "../../services/school/lessonPlans/getLessonPlans";

const columnNamesMap: { [key: string]: string } = {
  subject:"Disciplina", 
  class:"Turma", 
  dateDisplay:"Data", 
  objectives:"Objectivos", 
  resources:"Recursos", 
  activities:"Actividades", 
};

export const ManageLessonPlans = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const subjectPlanData = useSelector((state: RootState) => state.subjectPlan);
  const mutation = useMutation({ mutationFn: createLessonPlan, mutationKey: 'createLessonPlan' });
  const mutationCreateLessonPlanDocument = useMutation({ mutationFn: createLessonPlanDocument, mutationKey: 'createLessonPlanDocument' });

  const queryClient = useQueryClient();
  const [itemToBeDeleted, setItemToBeDeleted] = useState<LessonPlanType | null>(null);
  const { data, status, refetch } = useGetLessonPlansQuery({
    PageNumber: currentPage, PageSize: 400,
    IdSubjectPlan: subjectPlanData.id,
    IdCourse: subjectPlanData.idCourse,
    IdTeacher: subjectPlanData.idTeacher,
    IdClass: subjectPlanData.idClass,
    IdSubject: subjectPlanData.idSubject
  });
  const mutationDelete = useMutation(updateLessonPlan, {
    onSuccess: () => {
      queryClient.invalidateQueries(Key_lessonPlans);
      toast.success('Plano de aula eliminado');
    },
    onError: () => {
      toast.error("Erro ao eliminar o plano de aula.")
    }
  });
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const openDeleteDialog = (s: LessonPlanType) => {
    setItemToBeDeleted(s);
  };
  const confirmDelete = () => {
    if (itemToBeDeleted) {
      const updatedSubscription = { ...itemToBeDeleted, status: false };
      mutationDelete.mutateAsync(updatedSubscription);
      setItemToBeDeleted(null);
    }
  };

  const handleClickEdit = async (lessonPlan: LessonPlanType) => {
    try {
      const {items} = await getLessonPlansDocuments({PageNumber:1, PageSize:100, IdLessonPlan: lessonPlan.id})
      if(items.length>0){
        const documents = items?.map((document: LessonPlanDocumentType) => {
         // const file = dataURLtoFile(document.docBase64, document.filename);
          return {
              ...document,
           //   file:file,
         //     docBase64:''
          };
      });
      lessonPlan.documents = documents
    }
      lessonPlan.description = lessonPlan.description??"";
      dispatch(setLessonPlanData(lessonPlan))
      navigate(routes.updateLessonPlan)
      
    } catch (error) {
      toast.error("Não foi possível realizar o seu pedido")
    } 
  };
  const handleClickBack = async () => {
    navigate(routes.getSubjectPlans)
  };
  const options = data?.items.map(() => [
    {
      label: 'Editar',
      onClick: (s: LessonPlanType) => handleClickEdit(s),
      icon: <Pencil1Icon />
    },
    {
      label: 'Excluir',
      onClick: (s: LessonPlanType) => openDeleteDialog(s),
      icon: <TrashIcon />
    },
    // {
    //   label: 'Imprimir',
    //   onClick: (s: LessonPlanType) => handlePrint(s),
    //   icon: <ArchiveIcon />
    // },

  ]) || [];
  const defaultValues:LessonPlanType ={
    status: true, 
    idTeacher: subjectPlanData.idTeacher, 
    idClass: subjectPlanData.idClass, 
    idSubject: subjectPlanData.idSubject, 
    idSubjectPlan: subjectPlanData.id, 
    date: new Date().toISOString().split('T')[0],
    documents:[]
  }
  const { control, handleSubmit, setValue, getValues, formState: { errors }, watch, trigger, reset } = useForm<LessonPlanType>({
    resolver: yupResolver(CreateLessonPlanFormValidation),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });
  const onSubmit = handleSubmit(async (data) => {
    
    try {
      
      const result = await mutation.mutateAsync(data);
      if (result) {
        if(data.documents?.length===0){
          toast.success("Plano de aula criado!");
          reset();
          refetch();
          return;
        }
        if(data.documents && data.documents?.length>0){
          const convertedToBase64Docs = await Promise.all(data.documents?.map(async (document, index) => {
            const file = document.file as File
            return getFile(file, file.name)
          }));
          await Promise.all(convertedToBase64Docs.map((cv) => {
            return mutationCreateLessonPlanDocument.mutateAsync({
              docBase64: cv.base64,
              filename: cv.name,
              status: true,
              idLessonPlan: result,
              description: "",

            })
          }))
        }
        toast.success("Plano de aula criado!");
        reset();
        refetch();
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'documents',
  });

  {
    if (!subjectPlanData || !subjectPlanData.id) {
      return (
        <EmptyState
          title="Sem plano de disciplina seleccionado"
          description="Escolha um plano de disciplina para gerir os respectivos planos de aula."
          actionLabel="Escolher plano"
          onAction={handleClickBack}
        />
      );

    }
  }
  
  return (
    <>
      <Card>
        <Flex direction="row" gap='6'>
          <Flex direction="column" gap="5" style={{ flex: 1 }}>
            <form onSubmit={onSubmit} noValidate>
              <Flex direction='column' gap='4'>
              <TextAreaFieldComponent name="objectives" label="Objectivos" control={control} errors={errors} />
              <TextAreaFieldComponent name="activities" label="Actividades" control={control} errors={errors} />
              <TextAreaFieldComponent name="resources" label="Recursos" control={control} errors={errors} />
              <DateFieldComponent
                name="date"
                label="Data"
                control={control}
                errors={errors}
              />
                <Flex direction="column" gap="3">
                  <Text as="label">Documentos</Text>
                  {fields.map((f, index) => (
                    <Box
                      key={index}
                      p="3"
                      style={{
                        border: '1px solid var(--gray-6)',
                        borderRadius: 'var(--radius-3)',
                        position: 'relative'
                      }}
                    >
                      <Controller
                        name={`documents.${index}.file` as const}
                        control={control}
                        render={({ field }) => (
                          <DragAndDropFileUpload
                            onFileDrop={(file) => {
                              field.onChange(file);
                            }}
                            accept="*"
                            selectedFile={field.value}
                            remove={() => {
                              remove(index);
                            }}
                          />
                        )}
                      />
                      {errors.documents?.[index]?.file && (
                        <Text as="p" color="red" size="1" mt="1">
                          {`${errors.documents[index]?.file?.message}`}
                        </Text>
                      )}
                      <Tooltip content="Remover documento">
                        <IconButton
                          type="button"
                          size="1"
                          variant="soft"
                          color="red"
                          aria-label="Remover documento"
                          style={{
                            position: 'absolute',
                            top: 'var(--space-2)',
                            right: 'var(--space-2)'
                          }}
                          onClick={() => {
                            remove(index);
                          }}
                        >
                          <TrashIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                  <Button
                    type="button"
                    variant="soft"
                    onClick={() => append({ file: null })}
                    style={{ alignSelf: 'start' }}
                  >
                    <PlusIcon /> Adicionar documento
                  </Button>
                </Flex>

                <Flex direction='row' gap='3' justify='end'>
                  <Button type="button" variant='soft' color='gray' onClick={handleClickBack} disabled={mutation.isLoading}>
                    <ChevronLeftIcon /> Voltar
                  </Button>
                  <Button type="submit" disabled={mutation.isLoading}>
                    {mutation.isLoading ? <Spinner /> : <EnterIcon />}
                    Registar
                  </Button>
                </Flex>
              </Flex>
            </form>
          </Flex>
          <Flex direction="column" gap="4" style={{ flex: 1 }}>
            <>
              <Lists
                status={status}
                TableComponent={() =>
                  <TableComponent
                    columns={Object.keys(columnNamesMap)}
                    items={data?.items.map(i=>{
                      return {...i, dateDisplay:formatDateForInput(i.date!)}
                    })}
                    options={options}
                    columnNamesMap={columnNamesMap}
                    totalPages={data?.totalPages || 1}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    emptyMessage="Ainda não existem planos de aula registados para esta disciplina."
                  />
                }
              //  handleClickCreate={handleClickCreate}
              // handleClickBack={handleClickBack}
              />
              <ConfirmAlertDialog
                open={!!itemToBeDeleted}
                onOpenChange={(open) => !open && setItemToBeDeleted(null)}
                title="Esta operação é irreversível"
                description="Pretende eliminar este plano de aula?"
                confirmLabel="Eliminar"
                item={itemToBeDeleted}
                onConfirm={confirmDelete}
                onCancel={() => setItemToBeDeleted(null)}
              />
            </>
          </Flex>
        </Flex>
      </Card>


    </>
  );
}
